import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import cn from 'classnames';
import { format as formatDate } from 'date-fns';
import { ColorCalendar, ColorCalendarError, Edit } from '../../icons';
import { useDevice } from '../../providers/Device';
import { useBiAction } from '../BiTracking';
import { BottomSheetContentLayout } from '../BottomSheet/BottomSheetContentLayout/BottomSheetContentLayout';
import { StandaloneBottomSheet } from '../BottomSheet/StandaloneBottomSheet/StandaloneBottomSheet';
import { Button } from '../Button';
import { Box, Column } from '../Layout';
import { Spinner } from '../Spinner';
import { MobileSummaryCard } from '../TextField/MobileSummaryCard';
import { TimePicker } from '../TimePicker';
import { Typography } from '../Typography';
import { DateCalendar } from './DateCalendar';
import styles from './DatePicker.module.scss';
import { GetDatePickerTID, isValidDate, matchFieldWidthModifier, violatedDateBound } from './DatePicker.utils';
import { PickerLocalization } from './PickerLocalization';
const DEFAULT_FORMAT = 'MMM d, yyyy';
const DEFAULT_DATE_TIME_FORMAT = 'MMM d, yyyy hh:mm a';
export function DatePicker(props) {
    var _ref;
    const { value, onChange, minDate, maxDate, shouldDisableDate, getDayMeta, highlightDay, withTime, timeStep, timeFormat, minTime, maxTime, shouldDisableTime, timeColumnLabel = 'Time', actions, label, helperText, error, minDateMessage, maxDateMessage, disableErrors, helperTextReserveSpace, format, disabled, loading, size = 'medium', onFocus, onBlur, onBack, inputBadge, headerFormat, fullWidth = true, inputAriaLabel, 'data-agent-target': agentTarget, mobileVariant = 'row', fluid, popperClassName, popperOffset, bi, testId } = props;
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const popperModifiers = useMemo(()=>[
            ...popperOffset != null ? [
                {
                    name: 'offset',
                    options: {
                        offset: [
                            0,
                            popperOffset
                        ]
                    }
                }
            ] : [],
            ...withTime === true ? [
                matchFieldWidthModifier
            ] : []
        ], [
        popperOffset,
        withTime
    ]);
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const { DatePickerTestIds } = useMemo(()=>GetDatePickerTID(testId), [
        testId
    ]);
    const isDisabled = disabled === true || loading === true;
    const violatedBound = violatedDateBound({
        value,
        minDate,
        maxDate,
        granularity: withTime === true ? 'minute' : 'day'
    });
    const outOfBoundsMessage = violatedBound === 'min' ? minDateMessage : violatedBound === 'max' ? maxDateMessage : undefined;
    const errorActive = (error === true || outOfBoundsMessage != null) && disableErrors !== true && !open;
    const helperTextDisplayed = disableErrors === true || open ? null : (_ref = outOfBoundsMessage !== null && outOfBoundsMessage !== void 0 ? outOfBoundsMessage : helperText) !== null && _ref !== void 0 ? _ref : null;
    const displayFormat = format !== null && format !== void 0 ? format : withTime === true ? DEFAULT_DATE_TIME_FORMAT : DEFAULT_FORMAT;
    const formattedValue = isValidDate(value) ? formatDate(value, displayFormat) : '';
    const fireBi = (date)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) == null) return;
        logBiAction({
            entityType: 'date_picker',
            entityName: bi.name,
            actionType: 'select',
            testId,
            data: _object_spread_props(_object_spread({}, bi.data), {
                selected_value: formatDate(date, 'yyyy-MM-dd')
            })
        });
    };
    // WHY: DateCalendar owns normalisation — forward unchanged so BI ISO format and null-on-invalid hold.
    const handleCalendarChange = (date)=>{
        // WHY: in time mode the day is half the value — the popover stays up for the time column.
        if (withTime !== true) setOpen(false);
        onChange(date);
        if (date != null) fireBi(date);
    };
    const handleInputClick = ()=>{
        if (isDisabled) return;
        setOpen(true);
    };
    // WHY: readOnly input skips Enter/Space→click synthesis; portal Escape doesn't bubble back to input.
    const handleInputKeyDown = (e)=>{
        if (isDisabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
            return;
        }
        if (e.key === 'Escape') {
            setOpen(false);
        }
    };
    const handleClose = ()=>setOpen(false);
    const handleTimeChange = (time)=>{
        onChange(time);
        if (time != null) fireBi(time);
    };
    // WHY: the outer picker reports both halves as one date_picker select; a nested bi would double it.
    // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull
    const silentBiProps = {
        noBi: true
    };
    const timeColumn = withTime === true ? /*#__PURE__*/ _jsx(Column, {
        className: styles.timeColumn,
        gap: 8,
        children: /*#__PURE__*/ _jsx(TimePicker, _object_spread({
            value: value,
            onChange: handleTimeChange,
            format: timeFormat,
            timeStep: timeStep,
            minTime: minTime,
            maxTime: maxTime,
            shouldDisableTime: shouldDisableTime,
            listPresentation: isMobileDevice ? 'dropdown' : 'inline',
            disabled: isDisabled,
            label: timeColumnLabel,
            testId: DatePickerTestIds.Time(testId)
        }, silentBiProps))
    }) : null;
    const doneButton = withTime === true ? /*#__PURE__*/ _jsx(Box, {
        className: styles.doneRow,
        children: /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
            skin: "primary",
            size: "small",
            testId: DatePickerTestIds.Done(testId),
            onClick: handleClose
        }, silentBiProps), {
            children: "Done"
        }))
    }) : null;
    const calendarProps = {
        value,
        onChange: handleCalendarChange,
        minDate,
        maxDate,
        shouldDisableDate,
        getDayMeta,
        highlightDay,
        withTime,
        actions,
        headerFormat,
        testId
    };
    const handleKeyDown = (e)=>{
        if (e.key === 'Escape') handleClose();
    };
    // MUI helperText only renders when truthy — use a space to reserve layout without visible text.
    const helperTextRendered = helperTextDisplayed != null ? helperTextDisplayed : helperTextReserveSpace === true ? ' ' : null;
    const renderInputTrigger = ()=>/*#__PURE__*/ _jsx("div", {
            ref: anchorRef,
            className: cn(styles.inputWrap, {
                [styles.inputWrapSmall]: size === 'small'
            }),
            "data-testid": DatePickerTestIds.InputWrap(testId),
            "data-error": errorActive ? 'true' : undefined,
            onClick: handleInputClick,
            onKeyDown: handleInputKeyDown,
            children: /*#__PURE__*/ _jsx(TextField, {
                className: styles.textField,
                label: label,
                value: formattedValue,
                helperText: helperTextRendered,
                disabled: isDisabled,
                error: errorActive,
                size: size,
                fullWidth: fullWidth,
                onFocus: onFocus,
                onBlur: onBlur,
                InputProps: {
                    readOnly: true,
                    endAdornment: /*#__PURE__*/ _jsxs("span", {
                        className: styles.endAdornment,
                        children: [
                            inputBadge != null && /*#__PURE__*/ _jsx("span", {
                                "data-testid": DatePickerTestIds.InputBadge(testId),
                                children: inputBadge
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: styles.calendarAdornment,
                                "data-testid": DatePickerTestIds.Btn(testId),
                                children: loading === true ? /*#__PURE__*/ _jsx(Spinner, {
                                    size: 24,
                                    testId: DatePickerTestIds.Loading(testId)
                                }) : errorActive ? /*#__PURE__*/ _jsx(ColorCalendarError, {}) : /*#__PURE__*/ _jsx(ColorCalendar, {})
                            })
                        ]
                    })
                },
                // Cast: MUI's InputLabelProps does not enumerate data-* even though they pass through at runtime.
                InputLabelProps: {
                    'data-testid': DatePickerTestIds.Label(testId)
                },
                FormHelperTextProps: {
                    'data-testid': DatePickerTestIds.HelperText(testId)
                },
                inputProps: {
                    'data-testid': DatePickerTestIds.Input(testId),
                    'aria-label': label == null ? inputAriaLabel : undefined
                }
            })
        });
    const renderRowTrigger = ()=>/*#__PURE__*/ _jsxs(Box, {
            className: styles.row,
            testId: DatePickerTestIds.Row(testId),
            "data-error": errorActive ? 'true' : undefined,
            "data-disabled": isDisabled ? 'true' : undefined,
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: 12,
            onClick: handleInputClick,
            onKeyDown: handleInputKeyDown,
            onFocus: onFocus,
            onBlur: onBlur,
            role: "button",
            tabIndex: isDisabled ? -1 : 0,
            "aria-disabled": isDisabled || undefined,
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        label != null && /*#__PURE__*/ _jsx(Typography, {
                            as: "span",
                            variant: "paragraph3",
                            weight: "light",
                            skin: "secondary",
                            className: styles.rowLabel,
                            testId: DatePickerTestIds.RowLabel(testId),
                            children: label
                        }),
                        /*#__PURE__*/ _jsxs(Box, {
                            as: "span",
                            alignItems: "center",
                            gap: 8,
                            inline: true,
                            wrap: true,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    as: "span",
                                    variant: "paragraph3",
                                    weight: "light",
                                    skin: "standard",
                                    className: styles.rowValue,
                                    testId: DatePickerTestIds.RowValue(testId),
                                    children: formattedValue
                                }),
                                inputBadge != null && /*#__PURE__*/ _jsx(Box, {
                                    as: "span",
                                    testId: DatePickerTestIds.InputBadge(testId),
                                    inline: true,
                                    children: inputBadge
                                })
                            ]
                        }),
                        helperTextRendered != null && /*#__PURE__*/ _jsx(Typography, {
                            as: "span",
                            variant: "paragraph3",
                            weight: "light",
                            skin: "secondary",
                            className: styles.rowHelperText,
                            testId: DatePickerTestIds.HelperText(testId),
                            children: helperTextRendered
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Box, {
                    as: "span",
                    className: styles.rowEditBtn,
                    testId: DatePickerTestIds.RowEditBtn(testId),
                    inline: true,
                    children: loading === true ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 20,
                        testId: DatePickerTestIds.Loading(testId)
                    }) : /*#__PURE__*/ _jsx(Edit, {
                        size: 20
                    })
                })
            ]
        });
    const renderCardTrigger = ()=>/*#__PURE__*/ _jsx(MobileSummaryCard, {
            testId: DatePickerTestIds.Row(testId),
            error: errorActive,
            items: [
                {
                    title: label,
                    titleTestId: DatePickerTestIds.RowLabel(testId),
                    onClick: isDisabled ? undefined : handleInputClick,
                    body: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                as: "span",
                                skin: errorActive ? 'error' : 'standard',
                                testId: DatePickerTestIds.RowValue(testId),
                                children: formattedValue
                            }),
                            inputBadge != null && /*#__PURE__*/ _jsx(Box, {
                                as: "span",
                                inline: true,
                                testId: DatePickerTestIds.InputBadge(testId),
                                children: inputBadge
                            }),
                            helperTextRendered != null && /*#__PURE__*/ _jsx(Typography, {
                                as: "span",
                                skin: "secondary",
                                testId: DatePickerTestIds.HelperText(testId),
                                children: helperTextRendered
                            })
                        ]
                    }),
                    actions: isDisabled ? undefined : [
                        {
                            type: 'edit',
                            testId: DatePickerTestIds.RowEditBtn(testId),
                            // WHY: inherent affordance opening the calendar bottom-sheet; dispatches no onChange, so it can't double-count with the date_picker select BI.
                            bi: {
                                name: 'edit'
                            },
                            onClick: handleInputClick
                        }
                    ]
                }
            ]
        });
    const useRowOnMobile = isMobileDevice && mobileVariant === 'row';
    const useCardOnMobile = isMobileDevice && mobileVariant === 'card';
    const trigger = useCardOnMobile ? renderCardTrigger() : useRowOnMobile ? renderRowTrigger() : renderInputTrigger();
    return /*#__PURE__*/ _jsx(PickerLocalization, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.pickerRoot, {
                [styles.pickerRootFluid]: fluid === true
            }),
            "data-testid": DatePickerTestIds.Root(testId) || undefined,
            "data-agent-target": agentTarget,
            children: [
                trigger,
                isMobileDevice ? /*#__PURE__*/ _jsx(StandaloneBottomSheet, {
                    open: open && !isDisabled,
                    onClose: handleClose,
                    showBackButton: onBack != null,
                    onBack: onBack,
                    testId: DatePickerTestIds.BottomSheet(testId),
                    children: /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
                        header: {
                            title: label !== null && label !== void 0 ? label : null,
                            titleTestId: DatePickerTestIds.BottomSheetTitle(testId)
                        },
                        children: [
                            /*#__PURE__*/ _jsx(DateCalendar, _object_spread_props(_object_spread({}, calendarProps), {
                                className: styles.calendarMobile
                            })),
                            timeColumn,
                            doneButton
                        ]
                    })
                }) : /*#__PURE__*/ _jsx(Popper, {
                    anchorEl: anchorRef.current,
                    open: open && !isDisabled,
                    placement: "bottom-start",
                    className: popperClassName !== null && popperClassName !== void 0 ? popperClassName : styles.calendarPopper,
                    modifiers: popperModifiers,
                    "data-testid": DatePickerTestIds.Popover(testId),
                    children: /*#__PURE__*/ _jsx(ClickAwayListener, {
                        onClickAway: handleClose,
                        children: /*#__PURE__*/ _jsxs("div", {
                            onKeyDown: handleKeyDown,
                            className: withTime === true ? styles.calendarWithTime : undefined,
                            children: [
                                /*#__PURE__*/ _jsxs(Box, {
                                    className: styles.calendarWithTimeBody,
                                    children: [
                                        /*#__PURE__*/ _jsx(DateCalendar, _object_spread({}, calendarProps)),
                                        timeColumn
                                    ]
                                }),
                                doneButton
                            ]
                        })
                    })
                })
            ]
        })
    });
}
